import styled from '@emotion/styled'
import { Form, useFormikContext } from 'formik'
import { motion } from 'framer-motion'
import { darken } from 'polished'
import React, { useContext, useEffect, useRef, useMemo } from 'react'

import { buildClientForm, options } from '../../forms/enquiry-form/client'
import sendIconSrc from '../../icons/send-icon.svg'
import { UserContext } from '../context/user-context'
import { handleSubmit, prewarm } from './enquiry-form-handler'
import EnquiryTypeField from './enquiry-type-field'
import Field from './field'
import FormContainer from './form-container'
import TelField from './tel-field'

import countryData from 'i18n-iso-countries/langs/en.json'

const countries = Object.entries(countryData.countries).map(
  ([alpha2, names]) => ({
    alpha2,
    name: Array.isArray(names) ? names[1] : names,
  }),
)

export default function EnquiryForm({
  enquiryType = null,
  expandable,
  expanded,
  expandedChildren,
  showAcknowledgements = false,
}) {
  const { locationData, campaignData } = useContext(UserContext)
  const detectedCountryCode = locationData?.countryCode

  const includeAcknowledgements = useMemo(
    () =>
      campaignData.audienceLevel === 'tofu' || showAcknowledgements === true,
    [campaignData.audienceLevel, showAcknowledgements],
  )

  const clientForm = useMemo(
    () =>
      buildClientForm({
        withAcknowledgements: includeAcknowledgements,
      }),
    [includeAcknowledgements],
  )

  const initialValues = useMemo(
    () => ({
      ...clientForm.initialValues,
      countryOfOrigin: locationData?.countryCode,
    }),
    [clientForm, locationData],
  )

  // Preconfigure form for use on service pages
  if (enquiryType !== null) initialValues.enquiryType = enquiryType

  const firstInputRef = useRef(null)

  useEffect(() => {
    if (expanded && firstInputRef) firstInputRef.current.focus()
  }, [expanded])

  const { schema, submitUrl, screenResponse } = clientForm

  return (
    <FormContainer
      schema={schema}
      initialValues={initialValues}
      handleSubmit={(formData) =>
        handleSubmit({
          formData,
          locationData,
          campaignData,
          submitUrl,
          screenResponse,
        })
      }
      prewarm={() => prewarm(submitUrl)}
    >
      {({ isSubmitting }) => (
        <Form name="enquiry-form">
          {includeAcknowledgements && (
            <ExpandedWrapper
              inert={expandable && !expanded ? '' : undefined}
              variants={expandedVariants}
              animate={expandable && !expanded ? 'collapsed' : 'expanded'}
              transition={{
                type: 'spring',
                damping: 25,
                stiffness: 150,
              }}
            >
              <FieldContainer>
                <Acknowledgements />
              </FieldContainer>
            </ExpandedWrapper>
          )}

          <FieldContainer>
            {!enquiryType && (
              <div className="expand">
                <EnquiryTypeField name="enquiryType" />
              </div>
            )}
            <Field
              innerRef={firstInputRef}
              name="submitter.name"
              type="text"
              label="Full name"
              autoComplete="name"
              required
            />
            <Field
              name="submitter.email"
              type="email"
              label="Email"
              autoComplete="email"
              required
            />
          </FieldContainer>

          <ExpandedWrapper
            inert={expandable && !expanded ? '' : undefined}
            variants={expandedVariants}
            animate={expandable && !expanded ? 'collapsed' : 'expanded'}
            transition={{
              type: 'spring',
              damping: 25,
              stiffness: 150,
            }}
          >
            <FieldContainer style={{ paddingTop: 20 }}>
              <Field
                name="submitter.tel"
                label="Telephone"
                fieldComponent={TelField}
                detectedCountryCode={detectedCountryCode}
                required
              />

              <Field
                as="select"
                name="countryOfOrigin"
                label="Country of origin"
                required
              >
                {countries.map(({ alpha2, name }) => (
                  <option key={alpha2} value={alpha2}>
                    {name}
                  </option>
                ))}
              </Field>

              <Field
                className="field--enquiry expand"
                name="enquiry"
                label="How can we help?"
                component="textarea"
                placeholder="Please tell us:&#13; • your immigration goals and any relevant history&#13; • your area of business and professional experience"
                required
              />
            </FieldContainer>

            <ButtonGroup>
              <SubmitButton
                className="button button--large"
                type="submit"
                enterKeyHint="send"
                disabled={isSubmitting}
              >
                <img
                  src={sendIconSrc}
                  alt=""
                  height="64"
                  width="64"
                  loading="lazy"
                  style={{
                    height: '.95em',
                    width: 'auto',
                    display: 'inline',
                    margin: '-0.2em 0.5em -0.1em -0.3em',
                  }}
                />
                Send
              </SubmitButton>
            </ButtonGroup>

            {expandedChildren && expandedChildren}
          </ExpandedWrapper>
        </Form>
      )}
    </FormContainer>
  )
}

const Acknowledgements = () => {
  const {
    values: { acknowledgements },
  } = useFormikContext()

  return (
    <AcknowledgementsPanel className="expand">
      <div>
        <p className="reqsMicrocopy">
          Please be aware that <em>we don't provide jobs</em>.
        </p>
        <InlineField
          type="checkbox"
          name="acknowledgements.englishLevel"
          label="I am able to speak English"
        />

        <p className="reqsMicrocopy">
          Help us recommend suitable visas by indicating the funds you can{' '}
          <em>realistically access</em> (we do not share this).
        </p>

        <InlineField
          as="select"
          name="acknowledgements.fundsAvailable"
          label="Funds available"
          required
        >
          <option>Select…</option>
          {options.fundsAvailableValues.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </InlineField>
        {acknowledgements?.fundsAvailable ===
          options.fundsAvailableValues[0] && (
          <p className="reqsMicrocopy warningText">
            ⚠️&ensp;Unfortunately the chance of securing a business visa with
            funds {options.fundsAvailableValues[0].toLowerCase()} is extremely
            low.
          </p>
        )}
      </div>
    </AcknowledgementsPanel>
  )
}

const ButtonGroup = styled.div`
  margin: 34px auto 0;
  margin-bottom: 16px;
  display: flex;
  justify-content: center;
  > :not(:last-child) {
    margin-right: 8px;
  }
`

const SubmitButton = styled.button`
  min-width: 170px;
`

const FieldContainer = styled.div`
  display: grid;
  justify-content: center;
  grid-gap: 20px 16px;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));

  .expand {
    grid-column: 1/-1;
  }

  .field--enquiry textarea {
    min-height: 10em;
    @media (min-width: ${(p) => p.theme.breakpoints.medium}px) {
      min-height: 8em;
    }
  }
`

const expandedVariants = {
  collapsed: {
    height: '0px',
    opacity: 0,
  },
  expanded: {
    height: 'auto',
    opacity: 1,
  },
}

const ExpandedWrapper = motion.div

const InlineField = styled(Field)`
  display: inline-flex;
  align-items: center;
  text-align: left;
  flex-wrap: wrap;
  gap: 0 0.3em;

  &:has(input[type='checkbox']) {
    flex-direction: row-reverse;
    flex-wrap: nowrap;

    label {
      cursor: pointer;
      text-wrap: wrap;
    }
    input {
      transform: scale(1.2);
    }
  }

  &:not(:has(input[type='checkbox'])) > *:has(label + span) {
    flex-direction: column;
    label {
      padding-bottom: 0;
    }
    span {
      padding-inline: 2px;
      align-self: end;
    }
  }

  select {
    flex: 1 1 fit-content;
    min-width: 0 !important;
  }

  label {
    padding: 4px 2px;
    text-wrap: nowrap;
  }
`

const AcknowledgementsPanel = styled.div`
  margin-bottom: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;

  border-radius: 8px;
  background: ${(p) => p.theme.colors.lightTint};
  border: 1px solid ${(p) => p.theme.colors.secondary};
  padding: 20px 16px;

  .heading {
    margin-top: 0;
    margin-bottom: 6px;
    color: ${(p) => darken(0.05, p.theme.colors.secondary)};
  }

  > div {
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
  }

  .reqsMicrocopy {
    /* display: block; */
    font-size: 0.95em;
    text-align: center;
    margin: 0.4em 0 0.5em;
    text-wrap: balance;

    > p:first-child {
      margin-top: 0;
    }
    > p:last-child {
      margin-bottom: 0;
    }

    em {
      font-style: normal;
      text-decoration: underline;
      font-weight: bold;
      /* color: ${(p) => p.theme.colors.alert}; */
    }
  }

  .warningText {
    margin-block: 0;
    font-weight: bold;
    font-size: 0.9em;
  }
`
